import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, MomentumBadge, Loader, EmptyState } from "@/components/common";
import { Radar, Sparkles, Trash2, TrendingUp, Target, DollarSign, Zap } from "lucide-react";

const REGIONS = [["US","United States"],["GB","United Kingdom"],["IN","India"],["CA","Canada"],["AU","Australia"],["DE","Germany"],["BR","Brazil"],["JP","Japan"]];

function CompBar({ level }) {
  const map = { low: [1, "bg-signal-green"], medium: [2, "bg-signal-yellow"], high: [3, "bg-brand"] };
  const [n, c] = map[level] || map.medium;
  return (
    <div className="flex gap-1 items-center">
      {[1,2,3].map((i) => <span key={i} className={`w-4 h-1.5 rounded-sm ${i <= n ? c : "bg-white/10"}`} />)}
      <span className="text-[10px] uppercase tracking-wider text-white/40 font-mono ml-1">{level} comp</span>
    </div>
  );
}

function TrendCard({ t, onUse, onPipeline, onDelete }) {
  return (
    <div className="bg-surface border border-white/10 rounded-md p-5 hover:border-white/25 hover:-translate-y-1 transition-all duration-200 flex flex-col" data-testid={`trend-card-${t.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <MomentumBadge momentum={t.momentum} />
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-3xl tracking-tight text-signal-green leading-none">{t.trend_score}</span>
          <span className="text-[9px] uppercase text-white/30 font-mono leading-none">opp<br/>score</span>
        </div>
      </div>
      <h3 className="font-heading text-lg leading-tight mb-2">{t.title}</h3>
      <p className="text-sm text-white/50 leading-relaxed mb-4 flex-1">{t.summary}</p>

      <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
        <div className="flex items-center gap-2 text-white/60"><TrendingUp className="w-3.5 h-3.5 text-signal-green" /> <span className="font-mono">{t.predicted_growth}</span></div>
        <div className="flex items-center gap-2 text-white/60"><DollarSign className="w-3.5 h-3.5 text-signal-yellow" /> <span className="font-mono">${t.monetization_potential}% $$</span></div>
      </div>
      <div className="mb-4"><CompBar level={t.competition} /></div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {(t.keywords || []).slice(0,4).map((k,i) => (
          <span key={i} className="text-[10px] font-mono text-white/40 border border-white/10 rounded px-2 py-0.5">#{k}</span>
        ))}
      </div>

      <div className="border-t border-white/10 pt-3 mb-4">
        <div className="text-[10px] uppercase tracking-wider text-white/30 font-mono mb-1 flex items-center gap-1"><Target className="w-3 h-3" /> Video Angle</div>
        <p className="text-xs text-white/60 leading-relaxed">{t.video_angle}</p>
      </div>

      <div className="flex gap-2 mt-auto">
        <button onClick={() => onUse(t)} data-testid={`use-trend-${t.id}`}
          className="flex-1 bg-brand hover:bg-brand-hover text-white text-sm font-medium rounded-md px-4 py-2 transition-colors flex items-center justify-center gap-2">
          <Sparkles className="w-4 h-4" /> Create Video
        </button>
        <button onClick={() => onPipeline(t)} data-testid={`pipeline-trend-${t.id}`} title="One-click: package + thumbnail + variants + schedule"
          className="bg-signal-yellow/10 border border-signal-yellow/30 text-signal-yellow hover:bg-signal-yellow/20 rounded-md px-3 transition-colors flex items-center">
          <Zap className="w-4 h-4" />
        </button>
        <button onClick={() => onDelete(t.id)} data-testid={`delete-trend-${t.id}`}
          className="text-white/40 hover:text-brand border border-white/10 hover:border-brand/40 rounded-md px-3 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function Trends() {
  const [niche, setNiche] = useState("");
  const [region, setRegion] = useState("US");
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [pipeline, setPipeline] = useState(null);
  const nav = useNavigate();

  const load = () => api.get("/trends").then((r) => setTrends(r.data.trends)).finally(() => setFetching(false));
  useEffect(() => { load(); }, []);

  const discover = async () => {
    if (!niche.trim()) { toast.error("Enter a niche or topic first"); return; }
    setLoading(true);
    try {
      const r = await api.post("/trends/discover", { niche, region });
      setTrends((prev) => [...r.data.trends, ...prev]);
      toast.success(`Found ${r.data.trends.length} trends for "${niche}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Discovery failed");
    } finally { setLoading(false); }
  };

  const useTrend = (t) => nav("/studio", { state: { topic: t.video_angle || t.title, niche: t.niche, angle: t.summary, trend_id: t.id } });
  const del = async (id) => { await api.delete(`/trends/${id}`); setTrends((p) => p.filter((x) => x.id !== id)); };

  const runPipeline = async (t) => {
    setPipeline(t.title);
    try {
      await api.post("/pipeline/run", { topic: t.video_angle || t.title, niche: t.niche, angle: t.summary, trend_id: t.id });
      toast.success("Pipeline complete — package, thumbnail, variants & schedule created");
      nav("/library");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pipeline failed");
    } finally { setPipeline(null); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      {pipeline && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="pipeline-overlay">
          <div className="bg-surface border border-white/10 rounded-md p-8 max-w-md text-center">
            <div className="w-12 h-12 rounded-md bg-signal-yellow/10 border border-signal-yellow/30 flex items-center justify-center mx-auto mb-4">
              <Zap className="w-6 h-6 text-signal-yellow pulse-dot" />
            </div>
            <h3 className="font-heading text-xl mb-2">Building your video pipeline</h3>
            <p className="text-sm text-white/50 mb-4 truncate">{pipeline}</p>
            <div className="space-y-1.5 text-xs font-mono text-white/40 text-left max-w-xs mx-auto">
              <div>→ Writing full package & script</div>
              <div>→ Rendering AI thumbnail</div>
              <div>→ Creating Shorts / TikTok / Reels variants</div>
              <div>→ Drafting your posting schedule</div>
            </div>
            <p className="text-[10px] text-white/30 mt-4 font-mono">This takes ~60-90 seconds…</p>
          </div>
        </div>
      )}
      <PageHeader eyebrow="AI + Real Signal" title="Trend Radar" testid="trends-header"
        subtitle="Combine live YouTube trending data with AI foresight to find topics before they blow up." />

      <div className="bg-surface border border-white/10 rounded-md p-5 mb-8 fade-up">
        <div className="flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1">
            <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block font-mono">Niche / Topic</label>
            <input data-testid="niche-input" value={niche} onChange={(e) => setNiche(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && discover()}
              placeholder="e.g. AI tools, personal finance, fitness for men over 40"
              className="w-full bg-[#0A0A0A] border border-white/10 text-white rounded-md px-4 py-2.5 focus:border-brand focus:ring-1 focus:ring-brand/50 transition-all outline-none text-sm" />
          </div>
          <div className="md:w-48">
            <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block font-mono">Region</label>
            <select data-testid="region-select" value={region} onChange={(e) => setRegion(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-white/10 text-white rounded-md px-4 py-2.5 focus:border-brand outline-none text-sm">
              {REGIONS.map(([code, name]) => <option key={code} value={code} className="bg-[#121212]">{name}</option>)}
            </select>
          </div>
          <button data-testid="discover-btn" onClick={discover} disabled={loading}
            className="bg-brand hover:bg-brand-hover disabled:opacity-50 text-white font-medium rounded-md px-6 py-2.5 text-sm transition-colors flex items-center justify-center gap-2 whitespace-nowrap">
            <Radar className="w-4 h-4" /> {loading ? "Scanning..." : "Run Radar"}
          </button>
        </div>
      </div>

      {loading && <Loader label="Forecasting trends" />}

      {fetching ? <Loader label="Loading" /> : trends.length === 0 && !loading ? (
        <EmptyState icon={Radar} title="No trends yet" hint="Enter a niche above and run the radar to surface high-opportunity video ideas." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 fade-up">
          {trends.map((t) => <TrendCard key={t.id} t={t} onUse={useTrend} onPipeline={runPipeline} onDelete={del} />)}
        </div>
      )}
    </div>
  );
}
